/**
 * Página de Gerenciamento de Endpoints
 * Sistema MEDICARE
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  IconButton,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  Select,
  Switch,
  Alert,
  AlertIcon,
  Tooltip,
  Grid,
  GridItem,
  InputGroup,
  InputRightElement,
  VStack,
  HStack,
  Spacer,
  useDisclosure,
  useToast,
  Spinner,
  Flex,
  Heading,
  Divider,
  FormErrorMessage,
} from '@chakra-ui/react';
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiSearch,
  FiRefreshCw,
  FiPlay,
  FiEye,
  FiEyeOff,
} from 'react-icons/fi';
import { EndpointsService, Endpoint, CriarEndpointData, AtualizarEndpointData, FiltroEndpoints } from '../services/endpoints';

interface EndpointFormData {
  nome: string;
  url: string;
  metodo: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  descricao: string;
  requer_autenticacao: boolean;
  tipo_auth: string;
  usuario: string;
  senha: string;
}

interface FormErrors {
  nome?: string;
  url?: string;
  metodo?: string;
  descricao?: string;
  tipo_auth?: string;
  usuario?: string;
  senha?: string;
}

// Tipos de autenticação disponíveis
const TIPOS_AUTH = [
  { value: 'Basic Auth', label: 'Basic Auth' },
  { value: 'Bearer Token', label: 'Bearer Token' },
  { value: 'API Key', label: 'API Key' },
  { value: 'OAuth 2.0', label: 'OAuth 2.0' },
  { value: 'JWT', label: 'JWT' },
  { value: 'Digest Auth', label: 'Digest Auth' },
  { value: 'NTLM', label: 'NTLM' },
  { value: 'AWS Signature', label: 'AWS Signature' },
  { value: 'Custom Header', label: 'Custom Header' },
  { value: 'None', label: 'Nenhuma' },
];

const EndpointsManagementPage: React.FC = () => {
  const [endpoints, setEndpoints] = useState<Endpoint[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState<FiltroEndpoints>({});
  const [paginacao, setPaginacao] = useState({ pagina: 1, limite: 10, total: 0 });
  const [endpointSelecionado, setEndpointSelecionado] = useState<Endpoint | null>(null);
  const [modoEdicao, setModoEdicao] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [testando, setTestando] = useState<string | null>(null);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();

  const [formData, setFormData] = useState<EndpointFormData>({
    nome: '',
    url: '',
    metodo: 'GET',
    descricao: '',
    requer_autenticacao: false,
    tipo_auth: 'Basic Auth',
    usuario: '',
    senha: '',
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  useEffect(() => {
    carregarEndpoints();
  }, [filtros, paginacao.pagina]);

  const carregarEndpoints = async () => {
    try {
      setLoading(true);
      const response = await EndpointsService.listar(
        paginacao.pagina,
        paginacao.limite,
        filtros
      );
      setEndpoints(response.data);
      setPaginacao(prev => ({ ...prev, total: response.total }));
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar endpoints',
        description: error.response?.data?.mensagem || 'Erro interno do servidor',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const validarFormulario = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.nome.trim()) {
      errors.nome = 'Nome é obrigatório';
    }

    if (!formData.url.trim()) {
      errors.url = 'URL é obrigatória';
    } else {
      try {
        new URL(formData.url);
      } catch {
        errors.url = 'URL inválida';
      }
    }

    if (!formData.metodo) {
      errors.metodo = 'Método é obrigatório';
    }

    if (!formData.descricao.trim()) {
      errors.descricao = 'Descrição é obrigatória';
    }

    if (formData.requer_autenticacao) {
      if (!formData.usuario.trim()) {
        errors.usuario = 'Usuário é obrigatório quando autenticação é requerida';
      }
      if (!formData.senha.trim()) {
        errors.senha = 'Senha é obrigatória quando autenticação é requerida';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validarFormulario()) {
      return;
    }

    try {
      if (modoEdicao && endpointSelecionado) {
        const dadosAtualizacao: AtualizarEndpointData = {
          nome: formData.nome,
          url: formData.url,
          metodo: formData.metodo,
          descricao: formData.descricao,
          requer_autenticacao: formData.requer_autenticacao,
          tipo_auth: formData.tipo_auth,
          usuario: formData.requer_autenticacao ? formData.usuario : undefined,
          senha: formData.requer_autenticacao ? formData.senha : undefined,
        };
        await EndpointsService.atualizar(endpointSelecionado.id, dadosAtualizacao);
        toast({
          title: 'Endpoint atualizado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        const dadosCriacao: CriarEndpointData = {
          nome: formData.nome,
          url: formData.url,
          metodo: formData.metodo,
          descricao: formData.descricao,
          requer_autenticacao: formData.requer_autenticacao,
          tipo_auth: formData.tipo_auth,
          usuario: formData.requer_autenticacao ? formData.usuario : undefined,
          senha: formData.requer_autenticacao ? formData.senha : undefined,
        };
        await EndpointsService.criar(dadosCriacao);
        toast({
          title: 'Endpoint criado com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      onClose();
      carregarEndpoints();
    } catch (error: any) {
      toast({
        title: 'Erro ao salvar endpoint',
        description: error.response?.data?.mensagem || 'Erro interno do servidor',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleEditar = (endpoint: Endpoint) => {
    setEndpointSelecionado(endpoint);
    setModoEdicao(true);
    setFormData({
        nome: endpoint.nome,
        url: endpoint.url,
        metodo: endpoint.metodo,
        descricao: endpoint.descricao || '',
        requer_autenticacao: endpoint.requer_autenticacao || false,
        tipo_auth: endpoint.tipo_auth || 'Basic Auth',
        usuario: endpoint.usuario || '',
        senha: endpoint.senha || '',
      });
    setFormErrors({});
    onOpen();
  };

  const handleNovo = () => {
    setEndpointSelecionado(null);
    setModoEdicao(false);
    setFormData({
      nome: '',
      url: '',
      metodo: 'GET',
      descricao: '',
      requer_autenticacao: false,
      tipo_auth: 'Basic Auth',
      usuario: '',
      senha: '',
    });
    setFormErrors({});
    onOpen();
  };

  const handleExcluir = async (endpoint: Endpoint) => {
    if (window.confirm(`Tem certeza que deseja excluir o endpoint "${endpoint.nome}"?`)) {
      try {
        await EndpointsService.deletar(endpoint.id);
        toast({
          title: 'Endpoint excluído com sucesso',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        carregarEndpoints();
      } catch (error: any) {
        toast({
          title: 'Erro ao excluir endpoint',
          description: error.response?.data?.mensagem || 'Erro interno do servidor',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  };

  const handleAlterarStatus = async (endpoint: Endpoint) => {
    try {
      await EndpointsService.alterarStatus(endpoint.id, !endpoint.ativo);
      toast({
        title: `Endpoint ${!endpoint.ativo ? 'ativado' : 'desativado'} com sucesso`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      carregarEndpoints();
    } catch (error: any) {
      toast({
        title: 'Erro ao alterar status do endpoint',
        description: error.response?.data?.mensagem || 'Erro interno do servidor',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleTestarConectividade = async (endpoint: Endpoint) => {
    try {
      setTestando(endpoint.id);
      const resultado = await EndpointsService.testarConectividade(endpoint.id);
      
      const status = resultado.sucesso ? 'success' : 'error';
      const title = resultado.sucesso ? 'Teste bem-sucedido' : 'Teste falhou';
      const description = `Status: ${resultado.status_code} | Tempo: ${resultado.tempo_resposta}ms`;
      
      toast({
        title,
        description,
        status,
        duration: 5000,
        isClosable: true,
      });
    } catch (error: any) {
      toast({
        title: 'Erro ao testar conectividade',
        description: error.response?.data?.mensagem || 'Erro interno do servidor',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setTestando(null);
    }
  };

  const getStatusColor = (ativo: boolean) => {
    return ativo ? 'green' : 'red';
  };

  const getMetodoColor = (metodo: string) => {
    const colors: { [key: string]: string } = {
      GET: 'blue',
      POST: 'green',
      PUT: 'orange',
      DELETE: 'red',
      PATCH: 'purple',
    };
    return colors[metodo] || 'gray';
  };

  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Flex justify="space-between" align="center">
          <Heading size="lg">Gerenciamento de Endpoints</Heading>
          <Button
            leftIcon={<FiPlus />}
            colorScheme="blue"
            onClick={handleNovo}
          >
            Novo Endpoint
          </Button>
        </Flex>

        {/* Filtros */}
        <Card>
          <CardHeader>
            <Text fontSize="md" fontWeight="semibold">Filtros</Text>
          </CardHeader>
          <CardBody>
            <Grid templateColumns="repeat(auto-fit, minmax(200px, 1fr))" gap={4}>
              <FormControl>
                <FormLabel>Buscar</FormLabel>
                <InputGroup>
                  <Input
                    placeholder="Nome ou URL..."
                    value={filtros.busca || ''}
                    onChange={(e) => setFiltros({ ...filtros, busca: e.target.value })}
                  />
                  <InputRightElement>
                    <FiSearch />
                  </InputRightElement>
                </InputGroup>
              </FormControl>
              
              <FormControl>
                <FormLabel>Método</FormLabel>
                <Select
                  placeholder="Todos os métodos"
                  value={filtros.metodo || ''}
                  onChange={(e) => setFiltros({ ...filtros, metodo: e.target.value as any })}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </Select>
              </FormControl>
              
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  placeholder="Todos os status"
                  value={filtros.ativo !== undefined ? filtros.ativo.toString() : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setFiltros({ 
                      ...filtros, 
                      ativo: value === '' ? undefined : value === 'true' 
                    });
                  }}
                >
                  <option value="true">Ativo</option>
                  <option value="false">Inativo</option>
                </Select>
              </FormControl>
              
              <Flex align="end">
                <Button
                  leftIcon={<FiRefreshCw />}
                  onClick={carregarEndpoints}
                  isLoading={loading}
                >
                  Atualizar
                </Button>
              </Flex>
            </Grid>
          </CardBody>
        </Card>

        {/* Tabela */}
        <Card>
          <CardBody>
            {loading ? (
              <Flex justify="center" p={8}>
                <Spinner size="lg" />
              </Flex>
            ) : (
              <TableContainer>
                <Table variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Nome</Th>
                      <Th>URL</Th>
                      <Th>Método</Th>
                      <Th>Status</Th>
                      <Th>Tipo de Auth</Th>
                      <Th>Criado em</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {endpoints.map((endpoint) => (
                      <Tr key={endpoint.id}>
                        <Td>
                          <VStack align="start" spacing={1}>
                            <Text fontWeight="medium">{endpoint.nome}</Text>
                            <Text fontSize="sm" color="gray.500">
                              {endpoint.descricao}
                            </Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Text fontSize="sm" fontFamily="mono">
                            {endpoint.url}
                          </Text>
                        </Td>
                        <Td>
                          <Badge colorScheme={getMetodoColor(endpoint.metodo)}>
                            {endpoint.metodo}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(endpoint.ativo)}>
                            {endpoint.ativo ? 'Ativo' : 'Inativo'}
                          </Badge>
                        </Td>
                        <Td>
                          <Badge colorScheme={endpoint.requer_autenticacao ? 'orange' : 'gray'}>
                            {endpoint.tipo_auth || 'Basic Auth'}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="sm">
                            {new Date(endpoint.data_criacao).toLocaleDateString('pt-BR')}
                          </Text>
                        </Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="Testar conectividade">
                              <IconButton
                                aria-label="Testar"
                                icon={testando === endpoint.id ? <Spinner size="sm" /> : <FiPlay />}
                                size="sm"
                                colorScheme="green"
                                variant="ghost"
                                onClick={() => handleTestarConectividade(endpoint)}
                                isDisabled={testando === endpoint.id}
                              />
                            </Tooltip>
                            
                            <Tooltip label="Editar">
                               <IconButton
                                 aria-label="Editar"
                                 icon={<FiEdit2 />}
                                 size="sm"
                                 colorScheme="blue"
                                 variant="ghost"
                                 onClick={() => handleEditar(endpoint)}
                               />
                            </Tooltip>
                            
                            <Tooltip label={endpoint.ativo ? 'Desativar' : 'Ativar'}>
                              <Switch
                                isChecked={endpoint.ativo}
                                onChange={() => handleAlterarStatus(endpoint)}
                                size="sm"
                              />
                            </Tooltip>
                            
                            <Tooltip label="Excluir">
                              <IconButton
                                aria-label="Excluir"
                                icon={<FiTrash2 />}
                                size="sm"
                                colorScheme="red"
                                variant="ghost"
                                onClick={() => handleExcluir(endpoint)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            )}
            
            {endpoints.length === 0 && !loading && (
              <Flex justify="center" p={8}>
                <Text color="gray.500">Nenhum endpoint encontrado</Text>
              </Flex>
            )}
          </CardBody>
        </Card>
      </VStack>

      {/* Modal de Criação/Edição */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {modoEdicao ? 'Editar Endpoint' : 'Novo Endpoint'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isInvalid={!!formErrors.nome}>
                <FormLabel>Nome *</FormLabel>
                <Input
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  placeholder="Nome do endpoint"
                />
                <FormErrorMessage>{formErrors.nome}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!formErrors.url}>
                <FormLabel>URL *</FormLabel>
                <Input
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  placeholder="https://api.exemplo.com/endpoint"
                />
                <FormErrorMessage>{formErrors.url}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!formErrors.metodo}>
                <FormLabel>Método *</FormLabel>
                <Select
                  value={formData.metodo}
                  onChange={(e) => setFormData({ ...formData, metodo: e.target.value as any })}
                >
                  <option value="GET">GET</option>
                  <option value="POST">POST</option>
                  <option value="PUT">PUT</option>
                  <option value="DELETE">DELETE</option>
                  <option value="PATCH">PATCH</option>
                </Select>
                <FormErrorMessage>{formErrors.metodo}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!formErrors.descricao}>
                <FormLabel>Descrição *</FormLabel>
                <Input
                  value={formData.descricao}
                  onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                  placeholder="Descrição do endpoint"
                />
                <FormErrorMessage>{formErrors.descricao}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!formErrors.tipo_auth}>
                <FormLabel>Tipo de Autenticação *</FormLabel>
                <Select
                  value={formData.tipo_auth}
                  onChange={(e) => setFormData({ ...formData, tipo_auth: e.target.value })}
                >
                  {TIPOS_AUTH.map((tipo) => (
                    <option key={tipo.value} value={tipo.value}>
                      {tipo.label}
                    </option>
                  ))}
                </Select>
                <FormErrorMessage>{formErrors.tipo_auth}</FormErrorMessage>
              </FormControl>

              <FormControl>
                <HStack>
                  <Switch
                    isChecked={formData.requer_autenticacao}
                    onChange={(e) => setFormData({ ...formData, requer_autenticacao: e.target.checked })}
                  />
                  <FormLabel mb={0}>Requer autenticação</FormLabel>
                </HStack>
              </FormControl>

              {formData.requer_autenticacao && (
                <>
                  <FormControl isInvalid={!!formErrors.usuario}>
                    <FormLabel>Usuário *</FormLabel>
                    <Input
                      value={formData.usuario}
                      onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
                      placeholder="Usuário para autenticação"
                    />
                    <FormErrorMessage>{formErrors.usuario}</FormErrorMessage>
                  </FormControl>

                  <FormControl isInvalid={!!formErrors.senha}>
                    <FormLabel>Senha *</FormLabel>
                    <InputGroup>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.senha}
                        onChange={(e) => setFormData({ ...formData, senha: e.target.value })}
                        placeholder="Senha para autenticação"
                      />
                      <InputRightElement>
                        <IconButton
                          aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                          icon={showPassword ? <FiEyeOff /> : <FiEye />}
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowPassword(!showPassword)}
                        />
                      </InputRightElement>
                    </InputGroup>
                    <FormErrorMessage>{formErrors.senha}</FormErrorMessage>
                  </FormControl>
                </>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleSubmit}>
              {modoEdicao ? 'Atualizar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default EndpointsManagementPage;